/**
 * Zweidimensionale Netzkarte.
 *
 * Zeigt die eigene Topologie als Graph: Standorte, Router und deren Verbindungen
 * nach außen. Die Kanten färben sich nach Zustand und Auslastung, sodass ein
 * Engpass oder ein Ausfall auf einen Blick erkennbar ist – genau wie in einem
 * echten NOC-Dashboard.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Badge, Card, Field, Modal, Mono, PageHeader, StatusDot, Tabs } from '../components/ui';
import { useGame, useMetrics } from '../store/game';
import { useTheme } from '../store/auth';
import { dec, mbps, money, num, pct100 } from '../lib/format';

interface NodePayload extends Record<string, unknown> {
  label: string;
  sublabel: string;
  kind: 'datacenter' | 'router' | 'external' | 'ixp';
  tone: 'success' | 'warning' | 'danger' | 'muted' | 'info';
  detail?: string;
}

/** Eigene Knotendarstellung – React Flows Standardknoten passt farblich nicht. */
function TopologyNode({ data }: NodeProps) {
  const payload = data as NodePayload;
  const toneRing = {
    success: 'ring-accent-500',
    warning: 'ring-amber-500',
    danger: 'ring-red-500',
    muted: 'ring-panel-400',
    info: 'ring-sky-500',
  }[payload.tone];

  const icon = { datacenter: '▣', router: '⇄', external: '☁', ixp: '✳' }[payload.kind];

  return (
    <div className={`min-w-[9rem] rounded-md border border-panel-300 bg-white px-3 py-2 shadow-sm ring-1 dark:border-panel-700 dark:bg-panel-800 ${toneRing}`}>
      <Handle type="target" position={Position.Left} className="!h-2 !w-2 !border-0 !bg-panel-400" />
      <div className="flex items-center gap-2">
        <span className="text-sm opacity-70" aria-hidden>
          {icon}
        </span>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold">{payload.label}</p>
          <p className="truncate text-[10px] text-panel-500 dark:text-panel-400">{payload.sublabel}</p>
        </div>
      </div>
      {payload.detail && <p className="mt-1 truncate text-[10px] text-panel-400">{payload.detail}</p>}
      <Handle type="source" position={Position.Right} className="!h-2 !w-2 !border-0 !bg-panel-400" />
    </div>
  );
}

const nodeTypes = { topology: TopologyNode };

export function NetworkMap() {
  const world = useGame((s) => s.world);
  const live = useGame((s) => s.live);
  const metrics = useMetrics();
  const { dark } = useTheme();
  const [selectedLink, setSelectedLink] = useState<string | null>(null);
  const [view, setView] = useState<'topology' | 'geography'>('topology');

  const liveLinks = useMemo(() => new Map((live?.links ?? []).map((l) => [l.id, l])), [live]);

  const { nodes, edges } = useMemo(() => {
    if (!world) return { nodes: [] as Node[], edges: [] as Edge[] };

    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const datacenters = Object.values(world.datacenters);

    // Standorte: in der geografischen Ansicht an ihrer Kartenposition,
    // in der Topologieansicht gleichmäßig verteilt.
    datacenters.forEach((dc, index) => {
      const routers = Object.values(world.routers).filter((r) => r.datacenterId === dc.id);
      const position =
        view === 'geography'
          ? { x: dc.position.x * 1.1, y: dc.position.y * 0.9 }
          : { x: 320, y: index * 220 + 40 };

      nodes.push({
        id: `dc-${dc.id}`,
        type: 'topology',
        position,
        data: {
          label: dc.name,
          sublabel: `${dc.city} · ${routers.length} Router`,
          kind: 'datacenter',
          tone: dc.utilityPower ? 'success' : 'danger',
          detail: dc.utilityPower ? undefined : 'Netzstrom ausgefallen',
        } satisfies NodePayload,
      });

      routers.forEach((router, routerIndex) => {
        const routerPosition =
          view === 'geography'
            ? { x: dc.position.x * 1.1 + 40, y: dc.position.y * 0.9 + 90 + routerIndex * 70 }
            : { x: 320, y: index * 220 + 120 + routerIndex * 70 };

        nodes.push({
          id: `rtr-${router.id}`,
          type: 'topology',
          position: routerPosition,
          data: {
            label: router.name,
            sublabel: `AS${router.bgp.localAsn} · ${router.bgp.routerId}`,
            kind: 'router',
            tone: router.state === 'online' ? 'success' : router.state === 'degraded' ? 'warning' : 'danger',
            detail: `${router.bgp.neighbors.filter((n) => n.enabled).length} Sitzungen`,
          } satisfies NodePayload,
        });

        edges.push({
          id: `dc-rtr-${router.id}`,
          source: `dc-${dc.id}`,
          target: `rtr-${router.id}`,
          style: { stroke: dark ? '#3b4e64' : '#adbccd', strokeWidth: 1, strokeDasharray: '3 3' },
        });
      });
    });

    // Externe Gegenstellen und Verbindungen.
    const externals = new Map<string, { label: string; kind: 'external' | 'ixp' }>();

    Object.values(world.links).forEach((link, index) => {
      const liveLink = liveLinks.get(link.id);
      const state = liveLink?.state ?? link.state;
      const utilization = liveLink?.utilizationMbps ?? link.utilizationMbps;
      const peak = Math.max(utilization.aToB, utilization.bToA);
      const ratio = link.capacityMbps > 0 ? peak / link.capacityMbps : 0;

      const color =
        state === 'down'
          ? '#ef4444'
          : state === 'degraded'
            ? '#f59e0b'
            : ratio > 0.9
              ? '#ef4444'
              : ratio > 0.75
                ? '#f59e0b'
                : link.kind === 'transit'
                  ? '#f59e0b'
                  : link.kind.startsWith('peering')
                    ? '#3b82f6'
                    : '#21a471';

      let source: string | null = null;
      let target: string | null = null;

      if (link.a.kind === 'internal' && link.a.routerId) source = `rtr-${link.a.routerId}`;
      if (link.b.kind === 'internal' && link.b.routerId) target = `rtr-${link.b.routerId}`;

      // Externer Endpunkt bekommt einen eigenen Knoten.
      if (link.b.kind === 'external') {
        const id = `ext-${link.ixpId ?? link.remoteAsn ?? link.id}`;
        if (!externals.has(id)) {
          externals.set(id, { label: link.b.label, kind: link.ixpId ? 'ixp' : 'external' });
          nodes.push({
            id,
            type: 'topology',
            position: view === 'geography' ? { x: 780, y: 120 + externals.size * 90 } : { x: 640, y: index * 100 + 40 },
            data: {
              label: link.b.label,
              sublabel: link.ixpId ? 'Internet Exchange' : link.remoteAsn ? `AS${link.remoteAsn}` : 'extern',
              kind: link.ixpId ? 'ixp' : 'external',
              tone: 'info',
            } satisfies NodePayload,
          });
        }
        target = id;
      }

      if (!source || !target) return;

      edges.push({
        id: link.id,
        source,
        target,
        animated: state === 'up' && ratio > 0.02,
        label: `${mbps(peak)} / ${mbps(link.capacityMbps)}`,
        labelStyle: { fontSize: 9, fill: dark ? '#adbccd' : '#48607b' },
        labelBgStyle: { fill: dark ? '#1b232d' : '#ffffff', fillOpacity: 0.85 },
        style: { stroke: color, strokeWidth: Math.max(1.5, Math.min(5, ratio * 6 + 1.5)), strokeDasharray: state === 'down' ? '6 4' : undefined },
      });
    });

    return { nodes, edges };
  }, [world, liveLinks, dark, view]);

  const onEdgeClick = useCallback((_: unknown, edge: Edge) => setSelectedLink(edge.id), []);

  if (!world || !metrics) return null;
  const link = selectedLink ? world.links[selectedLink as never] : null;

  return (
    <>
      <PageHeader
        title="Netzkarte"
        description="Topologie, Auslastung und Zustand aller Strecken."
        actions={
          <Tabs
            tabs={[
              { id: 'topology', label: 'Topologie' },
              { id: 'geography', label: 'Geografisch' },
            ]}
            active={view}
            onChange={(id) => setView(id as 'topology' | 'geography')}
          />
        }
      />

      <Card padded={false} className="h-[calc(100vh-16rem)] min-h-[28rem] overflow-hidden">
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onEdgeClick={onEdgeClick}
            fitView
            proOptions={{ hideAttribution: true }}
            nodesDraggable
            nodesConnectable={false}
            edgesFocusable
            className="bg-panel-50 dark:bg-panel-950"
          >
            <Background color={dark ? '#334354' : '#d3dbe6'} gap={20} size={1} />
            <Controls className="!border-panel-300 !bg-white dark:!border-panel-700 dark:!bg-panel-800" showInteractive={false} />
            <MiniMap
              pannable
              zoomable
              className="!border-panel-300 !bg-white dark:!border-panel-700 dark:!bg-panel-900"
              nodeColor={(node) => {
                const tone = (node.data as NodePayload).tone;
                return tone === 'danger' ? '#ef4444' : tone === 'warning' ? '#f59e0b' : tone === 'info' ? '#3b82f6' : '#21a471';
              }}
            />
          </ReactFlow>
        </ReactFlowProvider>
      </Card>

      <div className="mt-3 flex flex-wrap gap-4 text-xs text-panel-500 dark:text-panel-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-6 bg-accent-500" /> Backbone
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-6 bg-amber-500" /> Transit
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-6 bg-sky-500" /> Peering
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-6 bg-red-500" /> überlastet oder ausgefallen
        </span>
        <span>Die Strichstärke zeigt die Auslastung. Ein Klick auf eine Kante öffnet die Details.</span>
      </div>

      {link && (
        <Modal open onClose={() => setSelectedLink(null)} title={link.name} wide>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Field label="Art">{link.kind}</Field>
            <Field label="Status">
              <StatusDot tone={link.state === 'up' ? 'success' : link.state === 'degraded' ? 'warning' : 'danger'} label={link.state} />
            </Field>
            <Field label="Kapazität">{mbps(link.capacityMbps)}</Field>
            <Field label="Auslastung (ausgehend)">{mbps(link.utilizationMbps.bToA)}</Field>
            <Field label="Auslastung (eingehend)">{mbps(link.utilizationMbps.aToB)}</Field>
            <Field label="Latenz">{dec(link.latencyMs, 2)} ms</Field>
            <Field label="Kosten/Monat">{money(link.monthlyCost)}</Field>
            <Field label="Mindestabnahme">{link.committedMbps ? mbps(link.committedMbps) : '–'}</Field>
            <Field label="Gegenstelle">{link.remoteAsn ? <Mono>AS{link.remoteAsn}</Mono> : link.b.label}</Field>
            <Field label="Ausfallsicherheit">{pct100(link.resilience * 100, 0)}</Field>
            <Field label="Grundverlust">{pct100(link.baseLossPercent, 2)}</Field>
            <Field label="Endpunkte">
              {link.a.label} ↔ {link.b.label}
            </Field>
          </div>
        </Modal>
      )}
    </>
  );
}
